import type { Environment, EnvironmentsResponse } from "../types";

export type PromotionSourceEnvironment = Exclude<Environment, "prod">;
export type PromotionTargetEnvironment = Exclude<Environment, "dev">;
export type EntrySection = "shell" | "services";

function getComparisonSourceEnvironment(envName: PromotionTargetEnvironment): PromotionSourceEnvironment {
  return envName === "stage" ? "dev" : "stage";
}

export function getPromotionTargetForSource(envName: Environment): PromotionTargetEnvironment | null {
  if (envName === "dev") return "stage";
  if (envName === "stage") return "prod";
  return null;
}

export function isPromotionSourceEnvironment(envName: Environment): envName is PromotionSourceEnvironment {
  return envName !== "prod";
}

export function isEnvironmentOutdated(
  data: EnvironmentsResponse,
  mfeName: string,
  envName: PromotionTargetEnvironment,
): boolean {
  const prevEnv = getComparisonSourceEnvironment(envName);
  const prev = data[prevEnv]?.mfes[mfeName];
  const curr = data[envName]?.mfes[mfeName];
  if (!prev || !curr) return false;
  return prev.resolved !== curr.resolved;
}

export function isEnvironmentMissing(
  data: EnvironmentsResponse,
  mfeName: string,
  envName: PromotionTargetEnvironment,
): boolean {
  const prevEnv = getComparisonSourceEnvironment(envName);
  const prev = data[prevEnv]?.mfes[mfeName];
  const curr = data[envName]?.mfes[mfeName];
  return Boolean(prev && !curr);
}

export function getComparisonMfeNames(
  data: EnvironmentsResponse,
  envName: PromotionTargetEnvironment,
): string[] {
  const prevEnv = getComparisonSourceEnvironment(envName);
  return [...new Set([
    ...Object.keys(data[prevEnv].mfes),
    ...Object.keys(data[envName].mfes),
  ])].sort();
}

export function getOutdatedMfes(
  data: EnvironmentsResponse,
  sourceEnv: PromotionSourceEnvironment,
): string[] {
  const comparisonEnv = getPromotionTargetForSource(sourceEnv);
  if (!comparisonEnv) return [];
  const mfeNames = Object.keys(data[sourceEnv].mfes);
  return mfeNames.filter(
    (name) => isEnvironmentOutdated(data, name, comparisonEnv),
  );
}

// --- Entry version comparison (shell, services) ---

export function isEntryOutdated(
  data: EnvironmentsResponse,
  name: string,
  section: EntrySection,
  envName: PromotionTargetEnvironment,
): boolean {
  const prevEnv = getComparisonSourceEnvironment(envName);
  const prev = data[prevEnv]?.[section]?.[name];
  const curr = data[envName]?.[section]?.[name];
  if (!prev || !curr) return false;
  return prev.version !== curr.version;
}

export function isEntryMissing(
  data: EnvironmentsResponse,
  name: string,
  section: EntrySection,
  envName: PromotionTargetEnvironment,
): boolean {
  const prevEnv = getComparisonSourceEnvironment(envName);
  const prev = data[prevEnv]?.[section]?.[name];
  const curr = data[envName]?.[section]?.[name];
  return Boolean(prev && !curr);
}

export function getComparisonEntryNames(
  data: EnvironmentsResponse,
  section: EntrySection,
  envName: PromotionTargetEnvironment,
): string[] {
  const prevEnv = getComparisonSourceEnvironment(envName);
  return [...new Set([
    ...Object.keys(data[prevEnv]?.[section] ?? {}),
    ...Object.keys(data[envName]?.[section] ?? {}),
  ])].sort();
}

export function getOutdatedEntries(
  data: EnvironmentsResponse,
  section: EntrySection,
  sourceEnv: PromotionSourceEnvironment,
): string[] {
  const comparisonEnv = getPromotionTargetForSource(sourceEnv);
  if (!comparisonEnv) return [];
  const names = Object.keys(data[sourceEnv]?.[section] ?? {});
  return names.filter(
    (name) => isEntryOutdated(data, name, section, comparisonEnv),
  );
}

// --- Version distance ---

export const SIGNIFICANT_VERSION_GAP = 5;

export function getVersionDistance(sourceVersion: string, targetVersion: string): number {
  const parse = (v: string) => {
    const parts = v.split("-")[0].split(".").map(Number);
    if (parts.length < 3 || parts.some(Number.isNaN)) return null;
    return { major: parts[0], minor: parts[1], patch: parts[2] };
  };

  const src = parse(sourceVersion);
  const tgt = parse(targetVersion);
  if (!src || !tgt) return 0;

  if (src.major !== tgt.major || src.minor !== tgt.minor) return 100;
  return Math.abs(src.patch - tgt.patch);
}

export function getSignificantlyOutdatedMfes(
  data: EnvironmentsResponse,
  sourceEnv: PromotionSourceEnvironment,
  threshold: number = SIGNIFICANT_VERSION_GAP,
): string[] {
  const targetEnv = getPromotionTargetForSource(sourceEnv);
  if (!targetEnv) return [];

  const mfeNames = Object.keys(data[sourceEnv].mfes);
  return mfeNames.filter((name) => {
    const src = data[sourceEnv].mfes[name];
    const tgt = data[targetEnv].mfes[name];
    if (!src || !tgt) return false;
    return getVersionDistance(src.resolved, tgt.resolved) >= threshold;
  });
}

export function getSignificantlyOutdatedEntries(
  data: EnvironmentsResponse,
  section: EntrySection,
  sourceEnv: PromotionSourceEnvironment,
  threshold: number = SIGNIFICANT_VERSION_GAP,
): string[] {
  const targetEnv = getPromotionTargetForSource(sourceEnv);
  if (!targetEnv) return [];

  const names = Object.keys(data[sourceEnv]?.[section] ?? {});
  return names.filter((name) => {
    const src = data[sourceEnv]?.[section]?.[name];
    const tgt = data[targetEnv]?.[section]?.[name];
    if (!src || !tgt) return false;
    return getVersionDistance(src.version, tgt.version) >= threshold;
  });
}